import { useRef, useEffect, type KeyboardEvent } from 'react';
import { ArrowUp, Stop, Paperclip } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

interface ChatInputProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onStop: () => void;
  isStreaming: boolean;
  disabled?: boolean;
}

export function ChatInput({ value, onChange, onSend, onStop, isStreaming, disabled }: ChatInputProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Auto-grow textarea
  useEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = 'auto';
    ref.current.style.height = `${Math.min(ref.current.scrollHeight, 160)}px`;
  }, [value]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isStreaming && value.trim()) onSend();
    }
  };

  const handleAttachClick = () => {
    toast('Attachments are coming soon!');
  };

  return (
    <div className="p-4 pb-8 w-full max-w-4xl mx-auto">
      <motion.div 
        layout
        className="flex flex-col p-3 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-slate-100 rounded-3xl focus-within:shadow-[0_8px_40px_rgba(0,0,0,0.08)] transition-all duration-300 relative z-20"
      >
        <textarea
          ref={ref}
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything about alumni, donors, or projects…"
          disabled={disabled}
          className="w-full resize-none bg-transparent px-2 py-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none transition-all leading-relaxed font-inter min-h-[44px]"
          style={{ maxHeight: '200px' }}
        />

        <div className="flex items-center justify-between mt-2 pt-1">
          <button
            onClick={handleAttachClick}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
            title="Attach file"
          >
            <Paperclip size={20} weight="regular" />
          </button>

          <motion.button
            layout
            whileTap={{ scale: 0.9 }}
            onClick={isStreaming ? onStop : onSend}
            disabled={!isStreaming && (!value.trim() || disabled)}
            className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 transition-colors shadow-sm ${
              isStreaming
                ? 'bg-slate-800 hover:bg-slate-900 text-white shadow-slate-900/20'
                : value.trim() && !disabled
                ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-500/20'
                : 'bg-slate-100 text-slate-300 cursor-not-allowed'
            }`}
          >
            <motion.div
              key={isStreaming ? 'stop' : 'send'}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {isStreaming ? <Stop size={16} weight="fill" /> : <ArrowUp size={16} weight="bold" />}
            </motion.div>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
